import { Pipe, PipeTransform } from '@angular/core';
import { DatePipe } from '@angular/common';

/**
 * Pipe que valida y formatea fechas de forma segura.
 * Si la fecha es inválida, retorna el valor por defecto en lugar de lanzar un error.
 */
@Pipe({
  name: 'safeDate',
  standalone: false
})
export class SafeDatePipe implements PipeTransform {
  private datePipe = new DatePipe('es-ES');
  private readonly dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/;

  /** @param timezone p. ej. `'UTC'` para fechas “solo día” en ISO medianoche (evita desfase por zona local). */
  transform(
    value: string | Date | number | null | undefined,
    format: string = 'dd-MM-yyyy',
    defaultValue: string = '',
    timezone?: string | null
  ): string {
    if (value === null || value === undefined || value === '') {
      return defaultValue;
    }

    if (typeof value === 'string' && (value === 'Invalid date' || value.toLowerCase() === 'invalid date')) {
      return defaultValue;
    }

    try {
      const dateOnlyValue = this.extractDateOnly(value);
      if (dateOnlyValue && format === 'dd-MM-yyyy') {
        const [year, month, day] = dateOnlyValue.split('-');
        return `${day}-${month}-${year}`;
      }

      const fechaDate = this.parseDate(value);

      if (!fechaDate) {
        return defaultValue;
      }

      const isDateOnly = !!dateOnlyValue;
      const fechaFormateada = this.datePipe.transform(
        fechaDate,
        format,
        timezone || (isDateOnly ? 'UTC' : undefined),
        'es-ES'
      );
      return fechaFormateada || defaultValue;
    } catch {
      return defaultValue;
    }
  }

  private extractDateOnly(value: string | Date | number): string | null {
    if (typeof value === 'string') {
      const datePart = value.substring(0, 10);
      return this.dateOnlyPattern.test(datePart) ? datePart : null;
    }

    if (typeof value === 'number') {
      const fechaDate = new Date(value);
      if (isNaN(fechaDate.getTime())) {
        return null;
      }
      const year = fechaDate.getUTCFullYear();
      const month = String(fechaDate.getUTCMonth() + 1).padStart(2, '0');
      const day = String(fechaDate.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    if (value instanceof Date && !isNaN(value.getTime())) {
      const year = value.getUTCFullYear();
      const month = String(value.getUTCMonth() + 1).padStart(2, '0');
      const day = String(value.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    return null;
  }

  private parseDate(value: string | Date | number): Date | null {
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === 'number') {
      const fechaDate = new Date(value);
      return isNaN(fechaDate.getTime()) ? null : fechaDate;
    }

    const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch.map(Number);
      return new Date(Date.UTC(year, month - 1, day));
    }

    const fechaDate = new Date(value);
    return isNaN(fechaDate.getTime()) ? null : fechaDate;
  }
}
